import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';
import {
	Link,
	useLocation,
	useParams
} from 'react-router-dom';
import {
	Header,
	Tab
} from 'semantic-ui-react';

import { log } from '../../../../../services/utils';
import {
	validateEmail,
	validatePassword,
	validatePhone,
	validateZip
} from '../../../../../services/validation';

import useForm from '../../../../../hooks/useForm';

import {
	getUser,
	createUser,
	updateUser
} from '../../../../../api/nora/users';

import {
	useAdminState,
	useAdminAction
} from '../../../../../contexts/AdminContext';
import { useUIAction } from '../../../../../contexts/UIContext';

import Alert from '../../../../Alert';
import AgenciesTab from './AgenciesTab';
import EmailValidationInfoPopup from '../../../../Form/EmailValidationInfoPopup';
import InfoTab from './InfoTab';
import NotificationsTab from './NotificationsTab';
import PasswordValidationInfoPopup from '../../../../Form/PasswordValidationInfoPopup';
import PhoneValidationInfoPopup from '../../../../Form/PhoneValidationInfoPopup';
import ProgramsTab from './ProgramsTab';

const UserDetails = () => {
	const { id } = useParams();
	const { state: navigationState } = useLocation();

	const {
		user,
		initialized
	} = useAdminState();
	const {
		setNotificationTypes,
		setUser,
		clearUser
	} = useAdminAction();
	const {
		setApiErrorAlert,
		setInfobar,
		setInfobarLoader,
		setNavLock
	} = useUIAction();

	const [ isLoading, setIsLoading ] = useState( true );
	const [ forceValidation, setForceValidation ] = useState( false );
	const [ activeIndex, setActiveIndex ] = useState( navigationState
		? navigationState.activeIndex
		: 0 );
	const [ pendingIndex, setPendingIndex ] = useState();
	const [ unsavedProgressAlert, setUnsavedProgressAlert ] = useState( false );
	const [ userSavedAlert, setUserSavedAlert ] = useState( false );

	const {
		formData,
		hasChanged: formDataHasChanged,
		resetFormData,
		setFormData,
		updateField
	} = useForm();

	useEffect( () => {
		navigationState?.to
			? setInfobar( <span><Link to={ navigationState.to }>{ navigationState.text || '\u2190\u00A0\u00A0Back' }</Link></span> )
			: setInfobar( <span>Administration &nbsp;&#9656;&nbsp; <Link to="/administration/users">Users</Link> &nbsp;&#9656;&nbsp; { user?.profile.firstName && user?.profile.lastName ? `${ user.profile.firstName } ${ user.profile.lastName }` : 'New User' }</span> );
	}, [
		navigationState,
		setInfobar,
		user
	] );

	useEffect( () => {
		if ( !initialized ) {
			setInfobarLoader( 'Please wait...' );
		} else if ( id > 0 ) {
			( async () => {
				setInfobarLoader( 'Retrieving User Info...' );

				try {
					const response = await getUser( id );

					setUser( response );
				} catch ( error ) {
					setApiErrorAlert( error );
				}
			} )();
		}

		return () => {
			clearUser();
		};
	}, [
		clearUser,
		id,
		initialized,
		setApiErrorAlert,
		setInfobarLoader,
		setUser
	] );

	useEffect( () => {
		if ( formDataHasChanged ) {
			setNavLock( true );
		} else {
			setNavLock( false );
		}
	}, [
		formDataHasChanged,
		setNavLock
	] );

	useEffect( () => {
		if ( user ) { // if it is not a new contact and once the contact object has been populated
			const {
				id,
				notificationSettings,
				photo,
				profile,
				role,
				userName
			} = user;

			setFormData( {
				id,
				photo,
				role: role.id,
				userName,
				// notification settings
				notificationDigestType: notificationSettings.digestType.id,
				...notificationSettings.events
					.reduce( ( acc, { id, active } ) => ( { ...acc, [ `event-${ id }` ]: active } ), {} ),
				emailNotifications: notificationSettings.email,
				smsNotifications: notificationSettings.sms,
				// profile
				address: profile.address,
				address2: profile.address2,
				city: profile.city,
				email: profile.email,
				firstName: profile.firstName,
				lastName: profile.lastName,
				organization: profile.organization.id,
				phone: profile.phone?.number,
				ext: profile.phone?.extension,
				SMSEnabled: profile.phone?.isSMSEnabled,
				state: profile.state,
				zip: profile.zip
			} );
		} else if ( id === '0' ) {
			setFormData( {
				id: 0,
				notificationDigestType: 97,
				emailNotifications: false,
				smsNotifications: false
			} );
		}
	}, [
		id,
		setFormData,
		user
	] );

	useEffect( () => {
		if ( formData.role ) {
			try {
				setNotificationTypes( formData.role );
			} catch ( error ) {
				setApiErrorAlert( error );
			}
		}
	}, [
		formData.role,
		setApiErrorAlert,
		setNotificationTypes
	] );

	useEffect( () => {
		if ( initialized && Object.keys( formData ).length ) {
			setIsLoading( false );
			setInfobarLoader( '' );
		}
	}, [
		formData,
		initialized,
		setInfobarLoader
	] );

	const errors = useMemo( () => ( {
		role: [
			{
				error: !formData.role,
				message: 'Role is a required field.',
				fieldLabel: 'Role'
			}
		],
		userName: [
			{
				error: !formData.userName,
				message: 'Username is a required field.',
				fieldLabel: 'Username'
			}
		],
		firstName: [
			{
				error: !formData.firstName,
				message: 'First Name is a required field.',
				fieldLabel: 'First Name'
			}
		],
		lastName: [
			{
				error: !formData.lastName,
				message: 'Last Name is a required field.',
				fieldLabel: 'Last Name'
			}
		],
		organization: [
			{
				error: !formData.organization,
				message: 'Organization is a required field.',
				fieldLabel: 'Organization'
			}
		],
		zip: [
			{
				error: Boolean( formData.zip ) && !validateZip( formData.zip ),
				message: 'Please enter a valid, 5-digit zip code.',
				fieldLabel: 'Zip'
			}
		],
		email: [
			{
				error: !formData.email,
				message: 'Email is a required field.',
				fieldLabel: 'Email'
			},
			{
				error: Boolean( formData.email ) && !validateEmail( formData.email ),
				message: <>Please enter a valid email address.&nbsp;<EmailValidationInfoPopup /></>,
				fieldLabel: 'Email'
			}
		],
		phone: [
			{
				error: !formData.phone,
				message: 'Phone is a required field.',
				fieldLabel: 'Phone'
			},
			{
				error: Boolean( formData.phone ) && !validatePhone( formData.phone ),
				message: <>Please enter a valid U.S. phone number.&nbsp;<PhoneValidationInfoPopup /></>,
				fieldLabel: 'Phone'
			}
		],
		pass: [
			{
				error: id === '0' && !formData.pass,
				message: 'Password is required to create a new user.',
				fieldLabel: 'Password'
			},
			{
				error: id === '0'
					&& Boolean( formData.pass )
					&& !validatePassword( formData.pass ),
				message: <>Please enter a valid password.&nbsp;<PasswordValidationInfoPopup password={ formData.pass } /></>,
				fieldLabel: 'Password'
			}
		],
		confirm: [
			{
				error: id === '0' && !formData.confirm,
				message: 'Must confirm password to create a new user.',
				fieldLabel: 'Confirm'
			},
			{
				error: id === '0'
					&& Boolean( formData.confirm )
					&& Boolean( formData.pass )
					&& formData.confirm !== formData.pass,
				message: <>Passwords must match.</>,
				fieldLabel: 'Confirm'
			}
		]
	} ), [
		formData.confirm,
		formData.email,
		formData.firstName,
		formData.lastName,
		formData.organization,
		formData.pass,
		formData.phone,
		formData.role,
		formData.userName,
		formData.zip,
		id
	] );

	const validate = useCallback( () => Object.values( errors ).some( errs => errs.some( err => err.error ) ), [ errors ] );

	const handleSave = useCallback( async cb => {
		if ( validate() ) {
			if ( !forceValidation ) {
				setForceValidation( true );
			}
		} else {
			setForceValidation( false );

			let {
				address,
				address2,
				city,
				email,
				ext,
				firstName,
				lastName,
				organization,
				emailNotifications,
				smsNotifications,
				notificationDigestType,
				phone,
				role,
				SMSEnabled,
				state,
				zip,
				...rest
			} = formData;

			const data = {
				// get everything that doesn't start with "event-"
				...Object.fromEntries( Object.entries( rest ).filter( entry => !entry[ 0 ].startsWith( 'event-' ) ) ),
				notificationSettings: {
					digestType: { id: notificationDigestType },
					email: emailNotifications,
					events: Object.entries( rest )
						.reduce( ( acc, [ k, v ] ) => {
							if ( k.startsWith( 'event-' ) ) {
								const id = k.slice( 'event-'.length );

								return [ ...acc, { id, active: v } ];
							}

							return acc;
						}, [] ),
					sms: smsNotifications
				},
				profile: {
					address,
					address2,
					city,
					email,
					firstName,
					lastName,
					organization: { id: organization },
					phone: {
						number: phone,
						extension: ext,
						isSMSEnabled: SMSEnabled
					},
					state,
					zip
				},
				role: { id: role }
			};

			try {
				setNavLock( false );

				if ( formData.id === 0 ) {
					const response = await createUser( data );

					setUser( response );
				} else {
					const response = await updateUser( data );

					setUser( response );
				}

				setUserSavedAlert( true );

				cb?.();
			} catch ( error ) {
				setNavLock( true );
				setApiErrorAlert( error );
			}
		}
	}, [
		forceValidation,
		formData,
		setApiErrorAlert,
		setNavLock,
		setUser,
		validate
	] );

	const menu = useMemo( () => ( {
		className: 'no-print',
		pointing: true,
		secondary: true,
		floated: true,
		style: {
			width: '100%',
			paddingTop: '0.5rem',
			marginBottom: '3rem'
		}
	} ), [] );

	const renderInfoTab = useCallback( () => (
		<InfoTab
			errors={ errors }
			forceValidation={ forceValidation }
			formData={ formData }
			formDataHasChanged= {formDataHasChanged }
			handleSave={ handleSave }
			resetFormData={ resetFormData}
			updateField={ updateField }
			validate={ validate }
		/>
	), [
		errors,
		forceValidation,
		formData,
		formDataHasChanged,
		handleSave,
		resetFormData,
		updateField,
		validate
	] );

	const renderProgramsTab = useCallback( ( { activeIndex } ) => (
		<ProgramsTab
			activeIndex={ activeIndex }
			formData={ formData }
			updateField={ updateField }
		/>
	), [
		formData,
		updateField
	] );

	const renderAgenciesTab = useCallback( ( { activeIndex } ) => (
		<AgenciesTab
			activeIndex={ activeIndex }
			formData={ formData }
			updateField={ updateField }
		/>
	), [
		formData,
		updateField
	] );

	const renderNotificationsTab = useCallback( () => (
		<NotificationsTab
			formData={ formData }
			formDataHasChanged= {formDataHasChanged }
			handleSave={ handleSave }
			resetFormData={ resetFormData}
			updateField={ updateField }
			validate={ validate }
		/>
	), [
		formData,
		formDataHasChanged,
		handleSave,
		resetFormData,
		updateField,
		validate
	] );

	const renderPanes = useCallback( () => {
		let panes = [
			{
				menuItem: 'Info',
				render: renderInfoTab
			},
			{
				menuItem: 'Notifications',
				render: renderNotificationsTab
			},
			...user?.programs.length
				? [
					{
						menuItem: 'Programs',
						render: renderProgramsTab
					}
				]
				: [],
			...user?.agencies.length
				? [
					{
						menuItem: 'Agencies',
						render: renderAgenciesTab
					}
				]
				: []
		];

		return panes;
	}, [
		renderAgenciesTab,
		renderInfoTab,
		renderNotificationsTab,
		renderProgramsTab,
		user?.agencies.length,
		user?.programs.length
	] );

	const handleUnsavedProgress = useCallback( async () => {
		await handleSave();
		setUnsavedProgressAlert( false );
		setActiveIndex( pendingIndex );
	}, [
		handleSave,
		pendingIndex
	] );

	const closeUnsavedProgressAlert = useCallback( () => {
		setUnsavedProgressAlert( false );
	}, [] );

	const closeUserSavedAlert = useCallback( () => {
		setUserSavedAlert( false );
	}, [] );

	const handleTabChange = useCallback( ( e, { activeIndex } ) => {
		if ( !validate() ) {
			if ( forceValidation ) {
				setForceValidation( false );
			}

			if ( formDataHasChanged ) {
				setPendingIndex( activeIndex );
				setUnsavedProgressAlert( true );
			} else {
				setActiveIndex( activeIndex );
			}
		} else if ( !forceValidation ) {
			setForceValidation( true );
		}
	}, [
		forceValidation,
		formDataHasChanged,
		validate
	] );

	if ( isLoading ) {
		return null;
	}

	return (
		<div className="detail-page">
			{ log( 'userDetails' ) }
			<Tab
				activeIndex={ activeIndex }
				menu={ menu }
				onTabChange={ handleTabChange }
				panes={ renderPanes() }
			/>
			<Alert
				action={ handleUnsavedProgress }
				actionText="Save and continue"
				name="user-unsaved-progress-confirm-alert"
				message="Please save your progress before continuing."
				open={ unsavedProgressAlert }
				closeModal={ closeUnsavedProgressAlert }
				closeText="Go back"
				type="confirm"
			/>
			<Alert
				closeModal={ closeUserSavedAlert }
				message={
					<Header
						as="h1"
						color="pink"
					>
						Saved!
					</Header>
				}
				name="user-saved-notification"
				open={ userSavedAlert }
				type="notification"
			/>
		</div>
	);
};

export default memo( UserDetails );
